/* @flow */
import React from 'react';

import Button from 'components/Button.jsx';
import CheckboxList from 'components/CheckboxList.jsx';
import Avatar from 'components/Avatar.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupInviteModal from 'groups/components/GroupInviteModal.jsx';
import GroupStore from 'groups/stores/GroupStore';
import Image from 'components/Image.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import LeadActions from 'actions/LeadActions';
import Leads from 'collections/Leads';
import LinkConstants from 'constants/LinkConstants';
import { mountModal } from 'lib/modal';
import Organization from 'entities/Organization';
import Organizations from 'collections/Organizations';
import ToastActions from 'actions/ToastActions';
import Tooltip from 'components/Tooltip.jsx';
import User from 'entities/User';

import './styles/Tymk.scss';

const helpText = `We work with third party companies that aggregate publicly available information
about schools (i.e. name, location) and school staff (i.e. names, email addresses) to generate these
lists. When you click Invite, Remind sends an email on your behalf to invite teachers based on your
specific selections. We will never send invitations without your permission. If you have any questions
about this list, please email ${LinkConstants.CONTACT_EMAIL}.`;

class Tymk extends React.Component {
  props: {
    leads: Leads,
    organizations: Organization[] | Organizations,
    triggeredContext: string,
    user: User,
    afterSendInvitations?: ?Function,
  }

  state: {
    leadIds: number[],
    checkedAll: boolean,
    staffClassToDirectAdd: ?Group,
  }

  state = {
    leadIds: this.getAllIds(),
    checkedAll: true,
    staffClassToDirectAdd: this.getStaffClassToDirectAdd(),
  }

  getAllIds() {
    return this.props.leads.getNamed().map(lead => lead.id);
  }

  getLeadOptions() {
    return this.props.leads.getNamed().reduce((result: Map<number, any>, lead) => { // eslint-disable-line
      const leadLabel = (
        <div className="Tymk-lead">
          <Avatar text={lead.getInitials()} colorHex={lead.getColor()} size="medium" className="Tymk-initials" />
          {lead.getDisplayName()}
        </div>
      );
      return result.set(lead.id, leadLabel);
    }, new Map());
  }

  getStaffClassToDirectAdd(): ?Group {
    let staffClass = null;
    this.props.organizations.forEach(organization => {
      if (!staffClass) {
        staffClass = GroupStore.getStaffClassForOrg(organization.id);
      }
    });
    return staffClass;
  }

  canSubmit() {
    return this.state.leadIds.length > 0;
  }

  goToDirectAdd = () => {
    if (this.props.afterSendInvitations) {
      this.props.afterSendInvitations();
    }
    mountModal(<GroupInviteModal group={this.state.staffClassToDirectAdd} initialTab={GroupInviteModal.TABS.INVITE} />, this);
  }

  handleLeadChange = (leadIds: number[]) => {
    this.setState({ leadIds });
  }

  sendInvitation = () => {
    const onSuccess = () => {
      ToastActions.showSuccessToast(
        'Thank you! All invitations have been sent.'
      );
    };

    LeadActions
      .sendInvitations(this.state.leadIds, this.props.triggeredContext)
      .then(onSuccess, ToastActions.showDefaultErrorToast)
      .then(() => {
        if (this.props.afterSendInvitations) {
          this.props.afterSendInvitations();
        }
      });
  }

  toggleAllCheckbox = () => {
    if (this.state.checkedAll) {
      this.setState({ leadIds: [], checkedAll: false });
    } else {
      this.setState({ leadIds: this.getAllIds(), checkedAll: true });
    }
  }

  renderFooter() {
    return (
      <div className="Tymk-footer">
        <Button
          className="Tymk-submitButton"
          disabled={!this.canSubmit()}
          onClick={this.sendInvitation}
          eventProperties={{
            selected: this.state.leadIds.length,
          }}
          eventElementName="Tymk-submitButton">
          Send invitations
        </Button>
        {this.renderHelpIcon()}
      </div>
    );
  }

  renderLeadsList() {
    return (
      <div className="Tymk-leadsBox">
        <div className="Tymk-leads">
          <CheckboxList
            options={this.getLeadOptions()}
            checked={this.state.leadIds}
            onChange={this.handleLeadChange}
            checkboxAlignment="right"
            showRowDividers
          />
        </div>
        {this.renderLeadsListButtons()}
      </div>
    );
  }

  renderLeadsListButtons() {
    const { staffClassToDirectAdd } = this.state;
    return (
      <div className="Tymk-leadsButtons">
        {staffClassToDirectAdd && this.renderInviteButton()}
        {this.renderSelectAllButton()}
      </div>
    );
  }

  renderInviteButton() {
    return (
      <Button
        className="inviteButton"
        level="text"
        onClick={this.goToDirectAdd}
        eventElementName="inviteButton">
        <Glyph name="avatar-add" color="brand" size="large" />
        Add teachers directly
      </Button>
    );
  }

  renderSelectAllButton() {
    const buttonText = this.state.checkedAll ?
      `Deselect all (${this.state.leadIds.length})` :
      `Select all (${this.props.leads.getNamed().length})`;
    return (
      <Button
        className="toggleButton"
        level="text"
        onClick={this.toggleAllCheckbox}
        eventElementName="toggleButton">
        {buttonText}
      </Button>
    );
  }

  renderTitle() {
    const { organizations } = this.props;
    return (
      <div className="Tymk-header">
        <Image src={require('assets/images/school-scene.svg')} />
        Share Remind with teachers at {organizations.length === 1 ? organizations[0].getName() : 'your schools'}!
      </div>
    );
  }

  renderHelpIcon() {
    return (
      <div className="Tymk-help-container">
        <LayerTrigger
          placement="bottom"
          layer={(
            <Tooltip
              className="Tymk-help-tooltip">
              {helpText}
            </Tooltip>
          )}>
          <Glyph size="large" name="help" color="eclipse" className="Tymk-help-glyph" />
        </LayerTrigger>
      </div>
    );
  }

  renderBody() {
    return (
      <div className="Tymk-body">
        {this.renderLeadsList()}
      </div>
    );
  }

  render() {
    return (
      <div className="Tymk">
        {this.renderTitle()}
        {this.renderBody()}
        {this.renderFooter()}
      </div>
    );
  }
}

export default Tymk;



// WEBPACK FOOTER //
// ./src/user/components/Tymk.jsx